import { format } from 'date-fns'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { ReactNode } from 'react'
import { useQuery } from 'react-query'
import {
  AssociatedFlexProjectLineModel,
  AssociatedFlexProjectModel,
} from '../../../../models/flex/AssociatedFlexProject.model'
import ProjectService from '../../../../services/ProjectService'
import { classNames } from 'primereact/utils'
import './RightPanel.scss'

type Props = {
  selectedProjectId: string | undefined
  isFlexProjectsLoading: boolean
}

const RightPanel = (props: Props) => {
  const { data, isFetching } = useQuery(
    ['associated-flex-projects', props.selectedProjectId],
    async () => {
      const result = await ProjectService.getAssociatedFlexProjects(props.selectedProjectId!)
      return result.data
    },
    {
      enabled: !!props.selectedProjectId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  )

  const renderProjectName = (rowData: AssociatedFlexProjectModel) => (
    <a
      href={`${process.env.REACT_APP_FLEX_PROJECT_URL}${rowData.projectId}`}
      target="_blank"
      className="text-blue-500 hover:text-blue-700 truncate no-underline"
      title={rowData.projectName!}
    >
      {rowData.projectName}
    </a>
  )

  const renderProjectECD = (rowData: AssociatedFlexProjectModel) =>
    rowData.projectECD ? format(new Date(rowData.projectECD), 'dd MMM yyyy') : null

  const renderOrderLines = ({ flexProjectLines }: AssociatedFlexProjectModel) => {
    const dataBodies: {
      body: (line: AssociatedFlexProjectLineModel) => ReactNode
      className?: string
    }[] = [
      {
        body: line => line.lineNumber,
        className: 'w-[35px] ',
      },
      {
        body: line => line.name,
        className: 'w-[280px] text-wrap',
      },
      {
        body: line => line.price! + ' ' + line.currency!,
        className: 'w-[60px] text-wrap',
      },
      {
        body: line => line.industryCode,
        className: 'w-[70px] text-wrap',
      },
      {
        body: line =>
          `${line.serviceCode} ${line.serviceCodeDescription ? `- ${line.serviceCodeDescription}` : ''}`,
        className: 'w-[210px] text-wrap',
      },
    ]

    return (
      <div className="flex flex-col w-full">
        {flexProjectLines.map((line, lineIndex) => (
          <div
            key={line.lineNumber}
            className={classNames(
              'flex items-center gap-4 min-h-[2.5rem] px-4',
              lineIndex !== flexProjectLines.length - 1 && 'border-b-2'
            )}
          >
            {dataBodies.map(({ body, className }, bodyIndex) => (
              <>
                <div key={bodyIndex} className={classNames(className, 'my-2')}>
                  {body(line)}
                </div>
                {bodyIndex !== dataBodies.length - 1 && (
                  <div className="border-l-2 border-gray-300 self-stretch my-2.5" />
                )}
              </>
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="p-[1rem] overflow-y-auto flex-grow">
      <div className="text-gray-600 font-bold mb-2">Associated Projects</div>

      <DataTable
        loading={isFetching || props.isFlexProjectsLoading || !props.selectedProjectId}
        value={data}
        className="w-full text-sm border border-gray-300"
        resizableColumns
        columnResizeMode="expand"
        autoLayout
        emptyMessage="No associated projects"
      >
        <Column
          field="projectName"
          header="Project Name"
          className="w-[15%] !items-start"
          body={renderProjectName}
        />
        <Column field="projectHandler" header="Project Handler" className="w-[15%] !items-start" />
        <Column
          field="projectECD"
          header="Project ECD"
          className="w-[10%] !items-start"
          body={renderProjectECD}
        />
        <Column field="projectStatus" header="Project Status" className="w-[15%] !items-start" />
        <Column
          field="flexProjectLines"
          header="Order Lines"
          body={renderOrderLines}
          className="w-[45%] !border-l-2 !border-l-gray-200"
          bodyClassName="!p-0"
          headerClassName="!grow"
        />
      </DataTable>
    </div>
  )
}

export default RightPanel
